import {
  CoverageTexts,
  InsuranceCoverage,
  SpecialInsuranceCoverage,
} from "Store/interfaces/products/coverage";
import { useDispatch, useSelector } from "react-redux";
import {
  AkaStudentCountries,
  CoverageCardText,
  TextLanguage,
  TextState,
} from "./interfaces";

import { ProductBackendName } from "Components/Features/CheckPrice/Products/productTypes";
import { INSURANCE_NAME_VALUE } from "Store/interfaces/products";
import CheckoutTexts from "Store/texts/checkoutTextInterface";
import { isDefined } from "Utils/functions";
import { ReduxState } from "../rootReducer";
import { BUY_INSURANCE_WEB_FMS_DOCUMENT } from "./constants";
import { textRequested } from "./slice";

export const useCheckPriceTexts = (): CheckoutTexts => {
  const dispatch = useDispatch();
  const {
    domain: { checkPrice },
    fetched,
    isLoading,
  } = useSelector((state: ReduxState) => state.texts);

  if (!fetched && !isLoading) {
    dispatch(textRequested(BUY_INSURANCE_WEB_FMS_DOCUMENT));
  }

  return checkPrice;
};

export const useTexts = (): TextState => {
  const dispatch = useDispatch();
  const { texts } = useSelector((state: ReduxState) => state);

  if (!texts.fetched && !texts.isLoading) {
    dispatch(textRequested(BUY_INSURANCE_WEB_FMS_DOCUMENT));
  }

  return texts;
};

export const useMessagesTexts = (): [any, TextLanguage] => {
  const texts = useTexts();
  return [texts.domain.messages, texts.language];
};

type IpidLinkCovGroup =
  | INSURANCE_NAME_VALUE
  | SpecialInsuranceCoverage
  | InsuranceCoverage
  | undefined;

export const useIpidLink = (
  coverageGroup: IpidLinkCovGroup,
): string | undefined => {
  const texts = useTexts();
  if (isDefined(coverageGroup)) {
    return texts.domain[`ipid.${coverageGroup}`];
  }
  return undefined;
};

type TermsLinkCovGroup =
  | INSURANCE_NAME_VALUE
  | SpecialInsuranceCoverage
  | InsuranceCoverage
  | undefined;

export const useInsuranceTermsPdfLink = (
  coverageGroup: TermsLinkCovGroup,
): string | undefined => {
  const texts = useTexts();
  if (isDefined(coverageGroup)) {
    return texts.domain[`insuranceTerms.${coverageGroup}`];
  }
  return undefined;
};

export const useAkaStudentCountries = (): AkaStudentCountries => {
  const texts = useTexts();
  return texts.domain["insurance_common.akastudent.countries"].studyPlace;
};

export const useInsuranceCoverages = (
  product: ProductBackendName,
): CoverageTexts[] => {
  const texts = useTexts();
  return texts.domain[`insuranceCoverages2.${product}`];
};

export const useProductTexts = <T = any>(
  product: string,
): [T, TextLanguage] => {
  const texts = useTexts();
  return [texts.domain.products[product], texts.language];
};

export const useTileTexts = (): [any, TextLanguage] => {
  const texts = useTexts();
  return [texts.domain.tiles, texts.language];
};

export const useOwnerTexts = (): [any, TextLanguage] => {
  const texts = useTexts();
  return [texts.domain.owner, texts.language];
};

export const usePriceTexts = (): [any, TextLanguage] => {
  const texts = useTexts();
  return [texts.domain.price, texts.language];
};

export const useErrorTexts = (): [any, TextLanguage] => {
  const texts = useTexts();
  return [texts.domain.errors, texts.language];
};

export const usePersonalInsuranceFormTexts = (): [any, TextLanguage] => {
  const texts = useTexts();
  return [texts.domain.personalInsuranceForm, texts.language];
};

export const useComponentTexts = (): [any, TextLanguage] => {
  const texts = useTexts();
  return [texts.domain.components, texts.language];
};

export const useButtonTexts = (): [any, TextLanguage] => {
  const texts = useTexts();
  return [texts.domain.buttons, texts.language];
};

export const useA11yTexts = (): [any, TextLanguage] => {
  const texts = useTexts();
  return [texts.domain.a11y, texts.language];
};

export const useValidationTexts = (): [any, TextLanguage] => {
  const texts = useTexts();
  return [texts.domain.validation, texts.language];
};

export const useProductTransferValues = (): [any, TextLanguage] => {
  const texts = useTexts();
  return [texts.domain.productTransfer, texts.language];
};

export const useSelectsTexts = (): [any, TextLanguage] => {
  const texts = useTexts();
  return [texts.domain.selects, texts.language];
};

export type EditProductURL = { oldUrl: string; newUrl: string };

export const useEditProductURLTexts = (): [
  Record<INSURANCE_NAME_VALUE, EditProductURL>,
  TextLanguage,
] => {
  const texts = useTexts();
  return [texts.domain.editProductURLs, texts.language];
};

export const useUrls = (): [any, TextLanguage] => {
  const texts = useTexts();
  return [texts.domain.URLs, texts.language];
};

export const useBreadcrumbs = (): [
  Record<string, Record<TextLanguage, string>>,
  TextLanguage,
] => {
  const texts = useTexts();
  return [texts.domain.breadcrumbs, texts.language];
};

export const useCoverageCardTexts = (
  product: string,
): [CoverageCardText, TextLanguage] => {
  const texts = useTexts();
  return [texts.domain.coverageCardTexts[product], texts.language];
};

export const useTextLanguage = (): TextLanguage => {
  const texts = useTexts();
  return texts.language;
};
